import axios, {
  AxiosDefaults,
  AxiosHeaderValue,
  AxiosInterceptorManager,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  HeadersDefaults,
} from 'axios'
import CryptoJS from 'crypto-js'
import qs from 'query-string'

import { ignoreErrorCodes } from '@/consts/errorMsg'
import { generateStableRequestId, TOKEN_CONFIG } from '@/consts/token'
import { useAccountsStore } from '@/stores/accounts'
import { useAuthStore } from '@/stores/auth'
import { useTeamStore } from '@/stores/team'
import { getActiveAccountId, getStoredAccounts, getToken } from '@/utils/auth'
import {
  formatErrCode,
  handleRequestError,
  handleResponseError,
  reportError,
  showError,
} from '@/utils/error'
import { tokenRefreshManager } from '@/utils/tokenRefreshManager'
import { isTeamTokenExpiredFromStore, isTeamTokenExpiringSoonFromStore } from '@/utils/tokenUtils'

type Res<T> = {
  [K in keyof T]: T[K]
} & { code: number; msg: string }

export interface RequestConf<D = any> extends AxiosRequestConfig<D> {
  errMsg?: boolean | string | Record<string, string>
  noErrMsg?: boolean
  domain?: number
  headers?: Record<string, string>
}

interface AxiosInstance {
  (config: RequestConf): AxiosPromise
  (url: string, config?: RequestConf): AxiosPromise
  interceptors: {
    request: AxiosInterceptorManager<RequestConf>
    response: AxiosInterceptorManager<AxiosResponse>
  }
  getUri(config?: RequestConf): string
  request<T = any>(config: RequestConf): Promise<Res<T>>
  get<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  delete<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  head<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  options<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  post<T = any>(url: string, data?: any, config?: RequestConf): Promise<Res<T>>
  put<T = any>(url: string, data?: any, config?: RequestConf): Promise<Res<T>>
  patch<T = any>(url: string, data?: any, config?: RequestConf): Promise<Res<T>>

  defaults: Omit<AxiosDefaults, 'headers'> & {
    headers: HeadersDefaults & {
      [key: string]: AxiosHeaderValue
    }
  }
}

function decryptData(xdatastring: string) {
  try {
    const aesBlockSize = 16
    const _key = 'be00XeK6E3hORuae85Lf5sYo2HXsJUeb'
    const iv = CryptoJS.enc.Utf8.parse(_key.substr(0, aesBlockSize))
    const ct = CryptoJS.enc.Base64.parse(xdatastring)
    const key = CryptoJS.enc.Utf8.parse(_key)
    const decrypted = CryptoJS.AES.decrypt({ ciphertext: ct } as any, key, {
      iv: iv,
    })
    const plainJSON = decrypted.toString(CryptoJS.enc.Utf8)
    return JSON.parse(plainJSON)
  } catch {
    return {}
  }
}

// Helper functions for request interceptor
function shouldSkipAuth(config: RequestConf): boolean {
  const isAuthApi =
    (config.method === 'post' && config.url === '/login') ||
    (config.method === 'post' && config.url === '/user')

  return isAuthApi || !!(config as any).__skipTokenRefresh
}

/**
 * Check if request is in team context (regardless of token validity)
 * This separates "user intent" from "token state"
 */
function isInTeamContext(config: RequestConf): boolean {
  // Binding API always uses platform context (to identify platform user)
  if (config.url === '/team/binding') {
    return false
  }

  const teamStore = useTeamStore.getState()
  // If teamTokenData exists (even if expired), user is in team context
  const hasTeamContext = !!teamStore.teamTokenData

  const isOnTeamPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/team/')
  const isTeamApiEndpoint = config.url?.startsWith('/team/') ?? false

  return hasTeamContext && (isOnTeamPage || isTeamApiEndpoint)
}

// Helper functions for response interceptor
async function handleTeamTokenExpiry(
  response: AxiosResponse,
  instance: AxiosInstance,
): Promise<AxiosResponse> {
  const requestId = (response.config as any).__requestId

  // Increment retry count BEFORE attempting refresh to prevent infinite loops
  const retryCount = tokenRefreshManager.incrementRetryCount(requestId)

  // Check if we've exceeded max retries AFTER incrementing
  if (tokenRefreshManager.hasExceededMaxRetries(requestId)) {
    console.error(
      `Team token refresh exceeded max retries (${retryCount}/${TOKEN_CONFIG.MAX_TOKEN_REFRESH_RETRIES})`,
    )
    handleTeamTokenRefreshFailure()
    throw response.data
  }

  try {
    // Refresh team token using TokenRefreshManager
    const newToken = await tokenRefreshManager.refreshTeamToken()

    // Update team token in Zustand store
    const teamStore = useTeamStore.getState()
    const currentTeamData = teamStore.teamTokenData
    if (currentTeamData) {
      // Use unified token expiry constant
      const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
      const expiryTime = Date.now() + TEAM_TOKEN_EXPIRY_MS
      teamStore.setTeamContext({
        ...currentTeamData,
        token: `Bearer ${newToken}`,
        expiryTime,
      })
    }

    // Retry original request with new token
    const originalRequest = response.config as any
    originalRequest.headers['Authorization'] = `Bearer ${newToken}`

    return instance(originalRequest)
  } catch (refreshError) {
    console.error(`Failed to refresh team token (attempt ${retryCount}):`, refreshError)

    // Pass the error to handleTeamTokenRefreshFailure for proper handling
    handleTeamTokenRefreshFailure(refreshError)

    throw response.data
  }
}

function handlePlatformTokenExpiry(): void {
  const accounts = getStoredAccounts()
  const activeAccountId = getActiveAccountId()

  if (accounts.length > 1 && activeAccountId) {
    // Remove the expired account and switch to another account
    useAccountsStore.getState().removeAccountById(activeAccountId)
  } else {
    // No other accounts, perform regular logout
    useAuthStore.getState().logout()
  }
}

function handleTeamTokenRefreshFailure(error?: any): void {
  const teamStore = useTeamStore.getState()
  const primaryTeamData = teamStore.primaryTeamTokenData
  const currentTeamData = teamStore.teamTokenData

  // Clear team context from store
  teamStore.clearTeamContext()

  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname

    // Check if this was a primary token expiry
    if (
      error?.message === 'PRIMARY_TOKEN_EXPIRED' ||
      (primaryTeamData && currentTeamData && primaryTeamData.teamId === currentTeamData.teamId)
    ) {
      // Original login team expired
      // Clear primary team context to prevent retry loops
      teamStore.clearPrimaryTeamContext()

      // Don't redirect if already on team login page
      if (currentPath !== '/team/login') {
        console.log('Primary team token expired, redirecting to team login')
        window.location.href = '/team/login'
      }
    } else {
      // Other cases - check if we have a platform token
      const platformToken = getToken()
      if (!platformToken) {
        // No valid tokens at all
        // Don't redirect if already on a login page
        if (currentPath !== '/login' && currentPath !== '/team/login') {
          window.location.href = '/login'
        }
      } else {
        // Platform token valid but can't get team token - go to platform
        if (currentPath !== '/create') {
          window.location.href = '/create'
        }
      }
    }
  }
}

export function withInterceptor(instance: AxiosInstance, source: string = 'platform') {
  instance.interceptors.request.use(async (config) => {
    if (!config.headers) {
      config.headers = {}
    }

    if (shouldSkipAuth(config)) {
      // For __skipTokenRefresh bypass, still set platform token
      if ((config as any).__skipTokenRefresh) {
        const token = getToken()
        if (token && !config.headers['Authorization']) {
          config.headers['Authorization'] = token
        }
      }
      return config
    }

    // Check if in team context (regardless of token validity)
    const inTeamContext = isInTeamContext(config)

    if (inTeamContext) {
      // In team context - handle token refresh before request
      if (isTeamTokenExpiredFromStore()) {
        // Token is expired, try to refresh before sending request
        if (!tokenRefreshManager.isRefreshing()) {
          try {
            const newToken = await tokenRefreshManager.refreshTeamToken()
            const teamStore = useTeamStore.getState()
            const currentTeamData = teamStore.teamTokenData
            if (currentTeamData) {
              const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
              teamStore.setTeamContext({
                ...currentTeamData,
                token: `Bearer ${newToken}`,
                expiryTime: Date.now() + TEAM_TOKEN_EXPIRY_MS,
              })
            }
            config.headers['Authorization'] = `Bearer ${newToken}`
          } catch (error) {
            // Refresh failed, let subsequent flow handle it
            console.error('Failed to refresh expired team token:', error)
          }
        } else {
          // Already refreshing, wait for it to complete
          try {
            const newToken = await tokenRefreshManager.refreshTeamToken()
            config.headers['Authorization'] = `Bearer ${newToken}`
          } catch (error) {
            console.error('Failed to get refreshed team token:', error)
          }
        }
      } else if (isTeamTokenExpiringSoonFromStore() && !tokenRefreshManager.isRefreshing()) {
        // Token expiring soon, proactively refresh
        try {
          const newToken = await tokenRefreshManager.refreshTeamToken()
          const teamStore = useTeamStore.getState()
          const currentTeamData = teamStore.teamTokenData
          if (currentTeamData) {
            const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
            teamStore.setTeamContext({
              ...currentTeamData,
              token: `Bearer ${newToken}`,
              expiryTime: Date.now() + TEAM_TOKEN_EXPIRY_MS,
            })
          }
          config.headers['Authorization'] = `Bearer ${newToken}`
        } catch (error) {
          console.warn('Proactive token refresh failed:', error)
        }
      }

      // Set token if not already set (from refresh or existing valid token)
      if (!config.headers['Authorization']) {
        const teamStore = useTeamStore.getState()
        const token = teamStore.teamTokenData?.token
        if (token) {
          config.headers['Authorization'] = token
        }
      }
    } else {
      // Platform context - use platform token
      const token = getToken()
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = token
      }
    }

    // Assign unique request ID for retry tracking
    ;(config as any).__requestId =
      (config as any).__requestId || generateStableRequestId(config.url, config.method)

    return config
  }, handleRequestError)

  instance.interceptors.response.use(
    async (response) => {
      if (typeof response.data?.['x-data'] == 'string') {
        const xData = decryptData(response.data?.['x-data'])
        response.data = Object.assign(response?.data, xData)
      }
      const { code } = response.data

      if (code === 0 || code === undefined) {
        // Successful response - reset retry count for this request
        const requestId = (response.config as any).__requestId
        if (requestId) {
          tokenRefreshManager.resetRetryCount(requestId)
        }

        if (response.data && typeof response.data === 'object') {
          response.data.source = source
        }
        return response.data
      }

      if (code === 176) {
        // Check if this request was in team context
        const teamStore = useTeamStore.getState()
        const hasTeamContext = !!teamStore.teamTokenData
        const isOnTeamPage =
          typeof window !== 'undefined' && window.location.pathname.startsWith('/team/')
        const isTeamApiEndpoint = response.config.url?.startsWith('/team/')

        // If in team context (has team token and on team page/endpoint), try team token refresh
        if (hasTeamContext && (isOnTeamPage || isTeamApiEndpoint)) {
          return handleTeamTokenExpiry(response, instance)
        } else {
          handlePlatformTokenExpiry()
        }
        throw response.data
      }

      const { errMsg = {} } = response.config as any
      const errMap = formatErrCode(errMsg)
      let err
      if (source && errMap[source]) {
        err = errMap[source][code] || errMap[source].default || errMap[code] || errMap.default
      } else {
        err = errMap[code] || errMap.default
      }

      // If using default error, append error code with offset
      if (!errMap[code] && err === errMap.default) {
        err = {
          ...err,
          msg: `${err.msg}: ${code + 10000}`,
        }
      }

      const sourceIgnoreCodes = ignoreErrorCodes[source] || []
      if (sourceIgnoreCodes.includes(code)) {
        // ignore
      } else {
        if (errMsg !== false && !!err.msg) {
          showError(err.msg, code, err.type, source)
        }
        reportError(response.data, err.msg, {
          url: response.config.url,
          method: response.config.method,
          data: response.config.data,
        })
      }

      return response.data
    },
    (error) => {
      const { errMsg = {} } = error.config || {}
      const errMap = formatErrCode(errMsg)
      error._matErrMap = errMap
      return handleResponseError(error, !error?.config?.noErrMsg)
    },
  )

  return instance
}

export function createInstance(
  baseURL: string,
  source: string = 'platform',
  needIntercept = true,
): AxiosInstance {
  const service = axios.create({
    baseURL,
    timeout: 1000 * 60,
    paramsSerializer: function (params) {
      return qs.stringify(params)
    },
  })

  if (needIntercept) {
    withInterceptor(service, source)
  }

  return service
}

export const API_BASE_URL =
  import.meta.env.VITE_ENV === 'development' ? '/api' : import.meta.env.VITE_API_BASE_URL

export default createInstance(API_BASE_URL, 'platform')
